'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Alert24CTA from '@/components/Alert24CTA';
import {
  EVENTS,
  CHANNELS,
  SEVERITIES,
  SEVERITY_META,
  type Channel,
  type Severity,
  type WinEvent,
} from './data';

type ChannelFilter = 'all' | Channel;
type SeverityFilter = 'all' | Severity;

/** Stable key for an event (a numeric ID can repeat across channels). */
function eventKey(e: WinEvent): string {
  return `${e.channel}-${e.id}`;
}

/** Build the Alert24 agent windows_event log-search block for one or more events. */
function buildAgentConfig(events: WinEvent[]): string {
  if (events.length === 0) return '';

  // Group selected events by channel — one log_search per channel, with the
  // event_ids array, mirroring docs/agent-log-searches.md.
  const byChannel = new Map<Channel, number[]>();
  for (const e of events) {
    const list = byChannel.get(e.channel) || [];
    if (!list.includes(e.id)) list.push(e.id);
    byChannel.set(e.channel, list);
  }

  const searches = Array.from(byChannel.entries()).map(([channel, ids]) => {
    const name = `${channel.toLowerCase()}_event_${ids.join('_')}`.slice(0, 60);
    return {
      name,
      source: 'windows_event',
      channel,
      event_ids: ids.sort((a, b) => a - b),
    };
  });

  return JSON.stringify({ log_searches: searches }, null, 2);
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          /* clipboard unavailable */
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md bg-[#2813e8] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#1f0fb8]"
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

export default function WindowsEventIdLookupTool() {
  const [query, setQuery] = useState('');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const didInit = useRef(false);

  // ── Deep-linking: read ?id= / ?channel= / ?q= on mount, write on change ──
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('id') || window.location.hash.replace('#', '');
    const qParam = params.get('q');
    const chParam = params.get('channel');
    if (qParam) setQuery(qParam);
    if (chParam && (CHANNELS as string[]).includes(chParam)) setChannel(chParam as Channel);
    if (idParam) {
      const idNum = parseInt(idParam, 10);
      if (!Number.isNaN(idNum)) {
        const match =
          (chParam && EVENTS.find((e) => e.id === idNum && e.channel === chParam)) ||
          EVENTS.find((e) => e.id === idNum);
        if (match) {
          setActiveKey(eventKey(match));
          // Scroll the detail into view after paint.
          setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        }
      }
    }
  }, []);

  // Keep the URL in sync with the active event for shareable deep links.
  useEffect(() => {
    if (!didInit.current || typeof window === 'undefined') return;
    const params = new URLSearchParams();
    const active = activeKey ? EVENTS.find((e) => eventKey(e) === activeKey) : null;
    if (active) {
      params.set('id', String(active.id));
      params.set('channel', active.channel);
    }
    const qs = params.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    try {
      window.history.replaceState(window.history.state, '', url);
    } catch {
      /* ignore */
    }
  }, [activeKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return EVENTS.filter((e) => {
      if (channel !== 'all' && e.channel !== channel) return false;
      if (severity !== 'all' && e.severity !== severity) return false;
      if (!q) return true;
      if (String(e.id).includes(q)) return true;
      const hay = `${e.title} ${e.description} ${e.whyMonitor} ${e.source} ${(e.keywords || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, channel, severity]);

  const selectedEvents = useMemo(
    () => EVENTS.filter((e) => selected[eventKey(e)]),
    [selected]
  );

  const activeEvent = useMemo(
    () => (activeKey ? EVENTS.find((e) => eventKey(e) === activeKey) || null : null),
    [activeKey]
  );

  const toggleSelect = (e: WinEvent) =>
    setSelected((prev) => ({ ...prev, [eventKey(e)]: !prev[eventKey(e)] }));

  const singleConfig = activeEvent ? buildAgentConfig([activeEvent]) : '';
  const multiConfig = buildAgentConfig(selectedEvents);

  return (
    <div className="space-y-6">
      {/* ── Controls ─────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <label htmlFor="weil-search" className="mb-1 block text-sm font-medium text-gray-700">
          Search by event ID or keyword
        </label>
        <input
          id="weil-search"
          type="text"
          inputMode="search"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          placeholder="e.g. 4625, lockout, kerberos, service crashed, bsod…"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#2813e8] focus:outline-none focus:ring-2 focus:ring-[#2813e8]/20"
        />

        <div className="mt-4 flex flex-wrap gap-4">
          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Channel
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(['all', ...CHANNELS] as ChannelFilter[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setChannel(c)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                    channel === c
                      ? 'border-[#2813e8] bg-[#2813e8] text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {c === 'all' ? 'All channels' : c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Severity
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(['all', ...SEVERITIES] as SeverityFilter[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSeverity(s)}
                  className={`rounded-full border px-3 py-1 text-sm capitalize transition-colors ${
                    severity === s
                      ? 'border-[#2813e8] bg-[#2813e8] text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {s === 'all' ? 'All severities' : SEVERITY_META[s].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          Showing <strong>{filtered.length}</strong> of {EVENTS.length} curated event IDs.
          {(query || channel !== 'all' || severity !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setChannel('all');
                setSeverity('all');
              }}
              className="ml-2 text-[#2813e8] underline hover:no-underline"
            >
              Reset filters
            </button>
          )}
        </p>
      </div>

      {/* ── Active event detail (deep-linkable) ──────────────────── */}
      {activeEvent && (
        <div
          ref={detailRef}
          id={`event-${activeEvent.id}`}
          className="scroll-mt-24 rounded-xl border-2 border-[#2813e8]/30 bg-[#2813e8]/[0.03] p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-gray-900 px-2.5 py-1 font-mono text-lg font-bold text-white">
                  {activeEvent.id}
                </span>
                <span className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
                  {activeEvent.channel}
                </span>
                <SeverityBadge severity={activeEvent.severity} />
              </div>
              <h3 className="mt-2 text-lg font-bold text-gray-900">{activeEvent.title}</h3>
              <p className="text-xs text-gray-500">Source / Provider: {activeEvent.source}</p>
            </div>
            <button
              type="button"
              onClick={() => setActiveKey(null)}
              className="text-sm text-gray-400 hover:text-gray-700"
              aria-label="Close detail"
            >
              Close ✕
            </button>
          </div>

          <p className="mt-3 text-sm text-gray-700">{activeEvent.description}</p>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              <strong>Why monitor it:</strong> {activeEvent.whyMonitor}
            </p>
          </div>

          {/* Agent config for the single event */}
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">
                Alert24 agent config — monitor this event
              </span>
              <CopyButton text={singleConfig} label="Copy config" />
            </div>
            <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs leading-relaxed text-green-300">
              <code>{singleConfig}</code>
            </pre>
          </div>
        </div>
      )}

      {/* ── Results table ────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2.5">Watch</th>
                <th className="px-3 py-2.5">ID</th>
                <th className="px-3 py-2.5">Channel</th>
                <th className="px-3 py-2.5">Title</th>
                <th className="px-3 py-2.5">Severity</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((e) => {
                const key = eventKey(e);
                return (
                  <tr
                    key={key}
                    className={`align-top transition-colors hover:bg-gray-50 ${
                      activeKey === key ? 'bg-[#2813e8]/[0.04]' : ''
                    }`}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={!!selected[key]}
                        onChange={() => toggleSelect(e)}
                        aria-label={`Add event ${e.id} (${e.channel}) to config`}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-[#2813e8] focus:ring-[#2813e8]"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveKey(key);
                          setTimeout(
                            () => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                            40
                          );
                        }}
                        className="font-mono font-semibold text-[#2813e8] hover:underline"
                      >
                        {e.id}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{e.channel}</td>
                    <td className="px-3 py-2.5 text-gray-800">{e.title}</td>
                    <td className="px-3 py-2.5">
                      <SeverityBadge severity={e.severity} />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveKey(key);
                          setTimeout(
                            () => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                            40
                          );
                        }}
                        className="text-xs text-gray-500 hover:text-[#2813e8] hover:underline"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-gray-500">
                    No events match your search. Try a different ID or keyword.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Multi-select config builder ──────────────────────────── */}
      {selectedEvents.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900">
              Alert24 agent config — {selectedEvents.length} selected event
              {selectedEvents.length === 1 ? '' : 's'}
            </h3>
            <div className="flex items-center gap-2">
              <CopyButton text={multiConfig} label="Copy config" />
              <button
                type="button"
                onClick={() => setSelected({})}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                Clear selection
              </button>
            </div>
          </div>
          <p className="mb-3 text-sm text-gray-600">
            Selected IDs are grouped into one <code className="rounded bg-gray-100 px-1">windows_event</code>{' '}
            search per channel.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs leading-relaxed text-green-300">
            <code>{multiConfig}</code>
          </pre>
        </div>
      )}

      {/* ── How to use the config ────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
        <h3 className="mb-2 text-base font-bold text-gray-900">How to add this to the Alert24 agent</h3>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
          <li>
            Install the lightweight Alert24 server agent on the Windows host (the PowerShell agent is the one
            that reads Windows Event Logs).
          </li>
          <li>
            Open the agent config JSON and paste the <code className="rounded bg-gray-100 px-1">log_searches</code>{' '}
            array above as a top-level key (sibling of <code className="rounded bg-gray-100 px-1">metrics</code> and{' '}
            <code className="rounded bg-gray-100 px-1">services</code>). If you already have a{' '}
            <code className="rounded bg-gray-100 px-1">log_searches</code> array, merge the objects in.
          </li>
          <li>
            Each interval the agent counts matching events with{' '}
            <code className="rounded bg-gray-100 px-1">Get-WinEvent</code> and reports a{' '}
            <code className="rounded bg-gray-100 px-1">match_count</code> back on the heartbeat. Add an alert rule
            (e.g. <code className="rounded bg-gray-100 px-1">log_match_count &gt; 10</code>) to get paged.
          </li>
        </ol>
        <p className="mt-3 text-xs text-gray-500">
          Log-search monitoring is an Alert24 paid (Pro/Enterprise) feature. The free plan includes 3 server
          agents; paid plans add 5 agents per subscription unit. Heartbeats are always accepted — log searches
          are simply evaluated only on a plan that includes the feature.
        </p>
      </div>

      {/* ── Product CTA ──────────────────────────────────────────── */}
      <Alert24CTA variant="log-search" source="windows-event-id-lookup" />
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const meta = SEVERITY_META[severity];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badge}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}
